import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getUserInfo, getSucursalInfo, hasPermission } from "../services/auth";
import { getInitials, renderMenuIcon } from "../masterLayout/MasterLayout";
import menuConfig from "../config/menuConfig";
import { useTranslation } from "../i18n/I18nContext";

// Shown in place of the Dashboard (DashBoardLayerEight) whenever the logged-in
// user's profile does not grant 'dashboard.view' — landing an authenticated
// user on an "Access Denied" page as their very first screen after login is
// a poor first impression, so instead this greets them and surfaces quick
// links to whatever they *do* have access to, derived from the same
// menuConfig + hasPermission() the sidebar itself uses.
const WelcomeScreenLayer = () => {
  const { t, locale } = useTranslation();
  const user = getUserInfo();
  const sucursal = getSucursalInfo();
  const [logoFailed, setLogoFailed] = useState(false);

  const greetingKey = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return "welcome.greeting.morning";
    if (hour < 18) return "welcome.greeting.afternoon";
    return "welcome.greeting.evening";
  }, []);

  const today = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(locale === "en" ? "en-US" : "pt-PT", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    const formatted = formatter.format(new Date());
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }, [locale]);

  // Flatten menuConfig into leaf items, keep only the ones this profile can
  // see, and drop 'dashboard.view' itself — this screen exists specifically
  // because the user doesn't have it.
  const quickLinks = useMemo(() => {
    const leaves = [];
    menuConfig.forEach((entry) => {
      const items = entry.type === "link" ? [entry] : entry.items;
      items.forEach((item) => {
        if (item.permissionCode === "dashboard.view") return;
        if (!hasPermission(item.permissionCode)) return;
        leaves.push(item);
      });
    });
    return leaves;
  }, []);

  const sucursalName = sucursal?.description || sucursal?.name || "";
  const roleName = user?.profile?.description || user?.authority || "";

  return (
    <div className='d-flex flex-column gap-24'>
      {/* Hero */}
      <div className='card border-0 radius-16 overflow-hidden'>
        <div className='bg-gradient-primary p-32 position-relative'>
          <div className='d-flex flex-wrap align-items-center justify-content-between gap-24'>
            <div className='d-flex align-items-center gap-16'>
              <div className='w-72-px h-72-px flex-shrink-0 rounded-circle bg-white d-flex justify-content-center align-items-center shadow-2 overflow-hidden'>
                {sucursal?.logoURL && !logoFailed ? (
                  <img
                    src={sucursal.logoURL}
                    alt={sucursalName}
                    className='w-100 h-100'
                    style={{ objectFit: "cover" }}
                    onError={() => setLogoFailed(true)}
                  />
                ) : (
                  <span className='fw-bold text-primary-600 h4 mb-0'>
                    {getInitials(user?.name)}
                  </span>
                )}
              </div>
              <div>
                <h3 className='mb-4 text-neutral-900'>
                  {t(greetingKey)}, {user?.name || t("global.name")}!
                </h3>
                <p className='mb-0 text-neutral-600 text-md'>{t("welcome.subtitle")}</p>
              </div>
            </div>

            <div className='d-flex flex-column align-items-md-end gap-8'>
              <span className='text-neutral-600 text-sm text-capitalize'>{today}</span>
              <div className='d-flex align-items-center gap-8 flex-wrap justify-content-end'>
                {sucursalName ? (
                  <span className='badge bg-white text-primary-600 border border-primary-200 px-16 py-8 radius-8 fw-medium d-inline-flex align-items-center gap-6'>
                    <i className='ri-building-4-line' /> {sucursalName}
                  </span>
                ) : null}
                {roleName ? (
                  <span className='badge bg-primary-600 text-white px-16 py-8 radius-8 fw-medium d-inline-flex align-items-center gap-6'>
                    <i className='ri-shield-user-line' /> {t("welcome.role.label")}: {roleName}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick access */}
      <div className='card border radius-16'>
        <div className='card-body p-24'>
          <h5 className='mb-4'>{t("welcome.quickAccess.title")}</h5>
          <p className='text-neutral-500 text-sm mb-24'>{t("welcome.quickAccess.subtitle")}</p>

          {quickLinks.length === 0 ? (
            <div className='text-center py-40'>
              <span className='mb-16 w-64-px h-64-px mx-auto bg-neutral-100 text-neutral-400 rounded-circle d-flex justify-content-center align-items-center h3'>
                <i className='ri-inbox-line' />
              </span>
              <h6 className='mb-8'>{t("welcome.quickAccess.empty.title")}</h6>
              <p className='text-neutral-500 text-sm mb-0'>{t("welcome.quickAccess.empty.subtitle")}</p>
            </div>
          ) : (
            <div className='row gy-16'>
              {quickLinks.map((item, index) => {
                const palette = ["cyan", "lilac", "success", "warning", "info", "primary"][index % 6];
                return (
                  <div className='col-xxl-3 col-xl-4 col-sm-6' key={item.permissionCode || item.path}>
                    <Link
                      to={item.path}
                      className='d-flex align-items-center gap-12 p-16 radius-12 border bg-hover-neutral-100 transition-2 text-decoration-none h-100'
                    >
                      <span
                        className={`w-48-px h-48-px flex-shrink-0 rounded-circle bg-${palette}-100 text-${palette}-600 d-flex justify-content-center align-items-center h5 mb-0`}
                      >
                        {renderMenuIcon(item.icon)}
                      </span>
                      <span className='fw-medium text-neutral-800 text-sm'>{t(item.label)}</span>
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <p className='text-center text-neutral-400 text-sm mb-0'>
        <i className='ri-lightbulb-line' /> {t("welcome.footer.tip")}
      </p>
    </div>
  );
};

export default WelcomeScreenLayer;
